/**
 * postOnUnload — fire a POST that survives the page being torn down.
 *
 * A normal axios/XHR call made from `beforeunload` / `pagehide` is routinely
 * cancelled by the browser as it destroys the document, so these "release the
 * lock on exit" calls frequently never reached the server at all. For
 * `/close-report` that meant `ow_reporting_status` stayed 'R' and the study kept
 * showing the live-report badge until the 2-minute server-side stale sweep
 * (cleanupInactiveStudies) cleared it.
 *
 * `fetch(..., { keepalive: true })` is the supported way to do this: the request
 * is handed to the browser's network stack and completes independently of the
 * page. `navigator.sendBeacon` is NOT usable here — it cannot set the
 * Authorization and x-csrf-token headers this API requires, so every beacon
 * would be rejected with a 401.
 *
 * Keepalive bodies are capped at 64KB by the spec; these are small JSON posts.
 * Fire-and-forget by design — there is no page left to handle a response.
 */
import { BASE_API } from '../axios';
import { TokenStorage } from './cookieStorage';

export const postOnUnload = (path: string, payload: Record<string, any>): boolean => {
  try {
    // Same base URL the axios instance uses, so this hits the identical endpoint.
    const url = `${BASE_API}${path}`;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    // Mirror the axios request interceptor — same auth + CSRF contract.
    const token = TokenStorage.getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const csrfToken = sessionStorage.getItem('csrf_token');
    if (csrfToken) {
      headers['x-csrf-token'] = csrfToken;
    }

    if (typeof fetch !== 'function') {
      return false;
    }

    // No .then() — the promise is abandoned with the page. .catch() only keeps a
    // rejected-promise warning out of the console on a dying document.
    fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers,
      body: JSON.stringify(payload),
    }).catch(() => {});

    return true;
  } catch {
    // Never let an exit-path call break the unload itself.
    return false;
  }
};

export default postOnUnload;
